import { API } from '../../api';
import { Button, Modal, Popconfirm, Table, notification } from 'antd';
import { DimaContext } from '../../DimaContext';
import { WarningOutlined } from '@ant-design/icons';
import { checkPermission, handleError } from '../../utils/util';
import EditableCell from './editableCell';
import EditableRow from './editableRow';
import React, { useContext, useEffect, useState } from 'react';

const { confirm } = Modal;

export default props => {
  const [dataSource, setDataSource] = useState(props.dataSource);
  const [count, setCount] = useState(0);
  const dimaContext = useContext(DimaContext);
  const [canEditExtractGuide, setCanEditExtractGuide] = useState(false);

  useEffect(() => {
    setDataSource(props.dataSource);
  }, [props.dataSource]);

  useEffect(() => {
    setCanEditExtractGuide(checkPermission(dimaContext.userData.permissions, 'editExtractGuide'));
  }, [dimaContext.userData.loaded]);

  const showSaveConfirm = () => {
    confirm({
      title: 'Are you sure?',
      icon: <WarningOutlined />,
      okText: 'Save Definitions',
      okType: 'primary',
      cancelText: 'Cancel',
      okButtonProps: { 'data-cy': 'save-extract-definition-btn' },
      onOk: async () => {
        const definitionsToSave = dataSource.map(definition => {
          return {
            allowed_values: definition[`allowed-${definition.id}`] || definition.allowed,
            column_name: definition[`column_name-${definition.id}`] || definition.column_name,
            description: definition[`description-${definition.id}`] || definition.description,
            nullable: definition[`nullable-${definition.id}`] || definition.nullable,
            required: definition[`required-${definition.id}`] || definition.required,
            resourcetype: definition.resource_type,
            format: definition[`type-${definition.id}`] || definition.type,
            version: props.selectedVersion,
          };
        });

        try {
          const response = await API.post('/extracts/definition', {
            resourceDefinitions: definitionsToSave,
          });

          if (response.status === 201) {
            notification.success({
              message: 'Success',
              description: 'Saved extract definitions',
            });
          }

          props.getResourceTypeDefinition();
          props.setEnableEdits(false);
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  const handleDelete = key => {
    const newData = dataSource.filter(item => item.key !== key);
    setDataSource(newData);
  };

  const defaultColumns = [
    {
      title: 'Column Name',
      dataIndex: 'column_name',
      width: '20%',
      editable: true,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        if (a.key <= 0) {
          return -1;
        }

        if (b.key <= 0) {
          return 1;
        }

        return a.column_name.localeCompare(b.column_name);
      },
    },
    {
      title: 'Required',
      dataIndex: 'required',
      width: '10%',
      editable: true,
    },
    {
      title: 'Nullable',
      dataIndex: 'nullable',
      width: '10%',
      editable: true,
    },
    {
      title: 'Allowed Values',
      dataIndex: 'allowed',
      width: '20%',
      editable: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: '30%',
      editable: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: '10%',
      editable: true,
    },
  ];

  if (props.enableEdits) {
    defaultColumns.push({
      title: 'Delete',
      render: (_, record) =>
        props.enableEdits ? (
          <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.key)}>
            <a href="#null">Delete</a>
          </Popconfirm>
        ) : (
          <span style={{ color: 'gray' }}>Delete</span>
        ),
    });
  }

  const handleAdd = () => {
    const newData = {
      key: count,
      column_name: '',
      required: null,
      nullable: null,
      allowed: '',
      description: '',
      type: '',
      format: '',
      resource_type: props.resourceType,
      version: 'latest',
    };
    setDataSource([...dataSource, newData]);
    setCount(count - 1);
  };

  const handleSave = row => {
    const newData = [...dataSource];
    const index = newData.findIndex(elem => row.key === elem.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: defaultProps => (
        <EditableCell dataSource={dataSource} enableEdits={props.enableEdits} {...defaultProps} />
      ),
    },
  };

  const editTable = () => {
    props.setEnableEdits(true);
  };

  const cancelEditTable = () => {
    confirm({
      title: 'Are you sure?',
      icon: <WarningOutlined />,
      content: 'Unsaved changes will be lost.',
      okText: 'Ok',
      okType: 'primary',
      cancelText: 'Continue editing',
      okButtonProps: { 'data-cy': 'cancel-extract-definition-btn' },
      onOk: () => {
        props.setEnableEdits(false);
        setDataSource(props.dataSource);
      },
    });
  };

  const columns = defaultColumns.map(col => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: record => ({
        record,
        editable: props.enableEdits,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div>
      {canEditExtractGuide && !props.enableEdits && !props.selectedVersion?.match(/^v\d+$/) && (
        <Button
          onClick={editTable}
          style={{ marginBottom: '10px' }}
          data-cy="edit-extract-guide-button"
        >
          Edit
        </Button>
      )}
      {canEditExtractGuide && props.enableEdits && (
        <>
          <Button onClick={showSaveConfirm} data-cy="save-extract-guide">
            Save
          </Button>
          <Button
            onClick={handleAdd}
            style={{ marginLeft: '10px', marginBottom: '10px' }}
            data-cy="insert-row-extract-guide-button"
          >
            Insert Row
          </Button>
          <Button
            onClick={cancelEditTable}
            style={{ marginLeft: '10px' }}
            data-cy="cancel-extract-guide-changes-button"
          >
            Cancel
          </Button>
        </>
      )}
      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 500, size: 'small' }}
        loading={props.loading}
        data-cy="editable-extract-guide-table"
      />
    </div>
  );
};
